export const QUEENS_SIZE = 6;
export const QUEENS_CELL_COUNT = 36;
export const QUEENS_REGION_COUNT = 6;

export type QueenCell = 0 | 1 | 2;

export type QueensPuzzle = {
  regions: number[];
  solution: number[];
};

export function isQueensPuzzle(value: unknown): value is QueensPuzzle {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<QueensPuzzle>;
  if (
    !Array.isArray(candidate.regions) ||
    candidate.regions.length !== QUEENS_CELL_COUNT ||
    !candidate.regions.every(
      (region) => Number.isInteger(region) && region! >= 0 && region! < QUEENS_REGION_COUNT,
    ) ||
    !Array.isArray(candidate.solution) ||
    candidate.solution.length !== QUEENS_SIZE ||
    !candidate.solution.every(
      (index) => Number.isInteger(index) && index! >= 0 && index! < QUEENS_CELL_COUNT,
    ) ||
    new Set(candidate.solution).size !== QUEENS_SIZE
  ) {
    return false;
  }

  const solution = candidate.solution;
  const rows = new Set(solution.map((index) => Math.floor(index / QUEENS_SIZE)));
  const columns = new Set(solution.map((index) => index % QUEENS_SIZE));
  const regions = new Set(solution.map((index) => candidate.regions![index]));
  if (rows.size !== 6 || columns.size !== 6 || regions.size !== 6) return false;
  const validQueens = solution.every((index, position) => {
    const row = Math.floor(index / QUEENS_SIZE);
    const column = index % QUEENS_SIZE;
    return solution.every((other, otherPosition) => {
      if (position === otherPosition) return true;
      const otherRow = Math.floor(other / QUEENS_SIZE);
      const otherColumn = other % QUEENS_SIZE;
      return Math.abs(row - otherRow) !== 1 || Math.abs(column - otherColumn) !== 1;
    });
  });
  if (!validQueens) return false;

  for (let region = 0; region < QUEENS_REGION_COUNT; region += 1) {
    const members = candidate.regions.flatMap((value, index) => (value === region ? [index] : []));
    if (members.length === 0) return false;
    const remaining = new Set(members);
    const pending = [members[0]];
    remaining.delete(members[0]);
    while (pending.length > 0) {
      const index = pending.pop()!;
      const row = Math.floor(index / QUEENS_SIZE);
      const column = index % QUEENS_SIZE;
      const neighbors = [
        row > 0 ? index - QUEENS_SIZE : -1,
        row < QUEENS_SIZE - 1 ? index + QUEENS_SIZE : -1,
        column > 0 ? index - 1 : -1,
        column < QUEENS_SIZE - 1 ? index + 1 : -1,
      ];
      for (const neighbor of neighbors) {
        if (remaining.delete(neighbor)) pending.push(neighbor);
      }
    }
    if (remaining.size > 0) return false;
  }
  return true;
}
